import { useState, useRef, useEffect } from 'react';
import {
  ChevronDown,
  Copy,
  ExternalLink,
  LogOut,
  Wallet,
  History,
  HelpCircle,
  Settings,
  Trophy,
  ImageIcon,
  Check,
} from 'lucide-react';
import { useDisconnect } from '@reown/appkit/react';

import { BackpackCampaignProgress } from '../BackpackCampaignProgress';
import { GateCampaignProgress } from '../GateCampaignProgress';
import { MagicEdenCampaignProgress } from '../MagicEdenCampaignProgress';
import { ReferralCampaignProgress } from '../ReferralCampaignProgress';
import { SolflareCampaignProgress } from '../SolflareCampaignProgress';

import { usePhantom } from '@/context/PhantomContext';
import { cn } from '@/lib/utils';
import { truncateAddress } from '@/lib/truncateAddress';

interface WalletDropdownProps {
  walletAddress: string;
  onHistoryClick: () => void;
  onFaqClick: () => void;
  onPrizeTiersClick: () => void;
  onSettingsClick: () => void;
  onNFTGalleryClick: () => void;
  isSolflareWallet?: boolean;
  isPhantomWallet?: boolean;
  isEmbeddedWallet?: boolean;
}

export function WalletDropdown({
  walletAddress,
  onHistoryClick,
  onFaqClick,
  onPrizeTiersClick,
  onSettingsClick,
  onNFTGalleryClick,
  isSolflareWallet = false,
  isPhantomWallet = false,
  isEmbeddedWallet = false,
}: WalletDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { disconnect } = useDisconnect();
  const { disconnectPhantom, isPhantomConnected } = usePhantom();

  // Handle copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy address:', error);
    }
  };

  // Handle view on explorer
  const handleViewExplorer = () => {
    const url = `https://explorer.solana.com/address/${walletAddress}`;
    window.open(url, '_blank');
    setIsOpen(false);
  };

  // Handle disconnect (Solana-only mode - no backend)
  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      // Disconnect based on which wallet is connected
      if (isPhantomConnected) {
        await disconnectPhantom();
      } else {
        await disconnect();
      }
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
    } finally {
      setIsDisconnecting(false);
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const buttonBackground = isPhantomWallet ? '#AB9FF2' : (isSolflareWallet ? '#FFEF46' : '#50E4F9');
  const buttonTextColor = isPhantomWallet ? 'white' : (isSolflareWallet ? '#000' : 'black');
  const buttonBorderColor = isPhantomWallet ? '#7C6FBF' : (isSolflareWallet ? '#D4BC3D' : '#2c2c2c');
  const buttonBoxShadow = isPhantomWallet
    ? '0 0 12px rgba(171,159,242,0.6)'
    : (isSolflareWallet ? '0 0 12px rgba(255,239,70,0.6)' : undefined);

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Wallet Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isDisconnecting}
        className={cn(
          'flex items-center space-x-2 px-3 py-2 rounded-lg border',
          'transition-colors',
          'text-sm font-medium',
          'w-auto md:w-full md:max-w-[160px] h-9',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          isOpen && 'bg-muted/50'
        )}
        style={{
          fontFamily: 'Hyperspace Race Capsule, cursive',
          backgroundColor: buttonBackground,
          color: buttonTextColor,
          borderColor: buttonBorderColor,
          boxShadow: buttonBoxShadow,
        }}
      >
        {isPhantomWallet ? (
          <img src="/Phantom-Icon_Square.svg" alt="Phantom" className="w-4 h-4 flex-shrink-0" />
        ) : isSolflareWallet ? (
          <img src="/solflare-icon.svg" alt="Solflare" className="w-4 h-4 flex-shrink-0" />
        ) : (
          <Wallet className="w-4 h-4 flex-shrink-0" style={{ color: buttonTextColor }} />
        )}
        {/* Show truncated address on desktop, hide on mobile */}
        <span className="hidden md:block flex-1 truncate text-left">{truncateAddress(walletAddress, 4)}</span>
        {/* Show checkmark on mobile only */}
        <Check className="md:hidden w-4 h-4 flex-shrink-0" style={{ color: buttonTextColor }} />
        {/* Show dropdown icon on desktop only */}
        <ChevronDown
          className={cn(
            'hidden md:block w-3 h-3 flex-shrink-0 transition-transform duration-200',
            isOpen && 'transform rotate-180'
          )}
          style={{ color: 'black' }}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className={cn(
            'absolute right-0 mt-2 w-64 py-2 rounded-lg shadow-lg',
            'animate-slide-in',
            'z-[9999]',
            'top-full'
          )}
          style={{
            backgroundColor: '#343434',
            border: '2px solid #50E5F8',
            boxShadow: '0 0 20px rgba(80, 228, 249, 0.3)',
          }}
        >
          {/* Wallet Info */}
          <div className="px-4 py-3" style={{ borderBottom: '1px solid rgba(80, 228, 249, 0.3)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif', color: '#50E5F8' }}>
                {isEmbeddedWallet ? 'YOUR WALLET ADDRESS' : 'CONNECTED WALLET'}
              </span>
              {isEmbeddedWallet && copied && (
                <span className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: '#4ADE80' }}>Copied!</span>
              )}
            </div>
            {isEmbeddedWallet ? (
              /* Full address display for embedded wallets - clickable to copy */
              <button
                onClick={handleCopy}
                className="w-full text-left p-2 rounded-lg transition-colors group"
                style={{ backgroundColor: 'rgba(80, 228, 249, 0.1)' }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.2)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(80, 228, 249, 0.1)'}
              >
                <span
                  className="font-mono text-xs break-all block"
                  style={{ fontFamily: 'monospace', color: 'white', lineHeight: '1.4' }}
                >
                  {walletAddress}
                </span>
                <div className="flex items-center gap-1 mt-2 text-xs" style={{ color: '#50E5F8' }}>
                  <Copy className="w-3 h-3" />
                  <span style={{ fontFamily: 'Outfit, sans-serif' }}>Tap to copy</span>
                </div>
              </button>
            ) : (
              <div className="flex items-center justify-between">
                <span className="font-mono text-sm" style={{ fontFamily: 'Outfit, sans-serif', color: 'white' }}>{truncateAddress(walletAddress, 4)}</span>
              </div>
            )}
          </div>

          {/* Campaign Progress Components - padding handled internally */}
          <BackpackCampaignProgress />
          <SolflareCampaignProgress />
          <MagicEdenCampaignProgress />
          <GateCampaignProgress />
          <ReferralCampaignProgress />

          {/* Menu Items */}
          <div className="py-1">
            {/* Navigation Items */}
            <button
              onClick={() => {
                onHistoryClick();
                setIsOpen(false);
              }}
              className="flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <History className="w-4 h-4" />
              <span>HISTORY</span>
            </button>

            <button
              onClick={() => {
                onPrizeTiersClick();
                setIsOpen(false);
              }}
              className="flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Trophy className="w-4 h-4" />
              <span>PRIZE TIERS</span>
            </button>

            <button
              onClick={() => {
                onNFTGalleryClick();
                setIsOpen(false);
              }}
              className="flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <ImageIcon className="w-4 h-4" />
              <span>MY NFTs</span>
            </button>

            <button
              onClick={() => {
                onFaqClick();
                setIsOpen(false);
              }}
              className="flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <HelpCircle className="w-4 h-4" />
              <span>HOW IT WORKS</span>
            </button>

            <button
              onClick={() => {
                onSettingsClick();
                setIsOpen(false);
              }}
              className="flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Settings className="w-4 h-4" />
              <span>SETTINGS</span>
            </button>

            {/* Divider */}
            <div className="my-1" style={{ borderTop: '1px solid rgba(80, 228, 249, 0.3)' }} />

            {/* Copy Address */}
            <button
              onClick={handleCopy}
              className="flex items-center justify-between w-full px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div className="flex items-center space-x-3">
                <Copy className="w-4 h-4" />
                <span>COPY ADDRESS</span>
              </div>
              {copied && <span className="text-xs" style={{ fontFamily: 'Outfit, sans-serif', color: '#50E5F8' }}>Copied!</span>}
            </button>

            {/* View on Explorer */}
            <button
              onClick={handleViewExplorer}
              className="flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: 'white' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <ExternalLink className="w-4 h-4" />
              <span>VIEW ON EXPLORER</span>
            </button>

            {/* Divider */}
            <div className="my-1" style={{ borderTop: '1px solid rgba(80, 228, 249, 0.3)' }} />

            {/* Disconnect */}
            <button
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              className="flex items-center space-x-3 w-full px-4 py-2 text-sm transition-colors disabled:opacity-50"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: '#DC2626' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(220, 38, 38, 0.1)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <LogOut className="w-4 h-4" />
              <span>{isDisconnecting ? 'DISCONNECTING...' : 'DISCONNECT'}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
